import React, { useState, useMemo } from 'react';
import { TideData } from '../../types/TidePoint';

interface TideChartProps {
  tideData: TideData;
}

const TideChart: React.FC<TideChartProps> = ({ tideData }) => {
  // Get available dates from tide data (convert UTC times to NZ dates)
  const availableDates = useMemo(() => {
    const dates = new Set<string>();
    tideData.tideEvents.forEach(event => {
      // Convert UTC time to NZ date
      const date = new Date(event.time).toLocaleDateString('en-CA', { timeZone: 'Pacific/Auckland' });
      dates.add(date);
    });
    return Array.from(dates).sort();
  }, [tideData]);

  const [selectedDate, setSelectedDate] = useState<string>(availableDates[0] || '');

  // Filter events for selected date + adjacent days for smooth curve
  // Note: API returns times in UTC, but we filter by NZ date
  const { dayEvents, displayEvents } = useMemo(() => {
    if (!selectedDate) return { dayEvents: [], displayEvents: [] };

    // Convert UTC times to NZ dates for filtering
    const getEventNZDate = (utcTimeStr: string) => {
      const date = new Date(utcTimeStr);
      return date.toLocaleDateString('en-CA', { timeZone: 'Pacific/Auckland' });
    };

    // Get events only for selected day (for schedule table)
    const dayEvents = tideData.tideEvents.filter(event => {
      const eventNZDate = getEventNZDate(event.time);
      return eventNZDate === selectedDate;
    }).sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());

    // For curve display, we need events that span beyond the visible day
    // to ensure the curve extends through the full 24-hour period
    // We'll filter by time range instead of date

    // Find midnight NZ time for the selected date
    const findNZMidnightForFilter = (dateStr: string) => {
      const baseUTC = new Date(dateStr + 'T00:00:00Z').getTime();

      for (let hourOffset = -14; hourOffset <= 14; hourOffset++) {
        const testUTC = baseUTC + (hourOffset * 3600000);
        const formatter = new Intl.DateTimeFormat('en-CA', {
          timeZone: 'Pacific/Auckland',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        });
        const nzString = formatter.format(new Date(testUTC));
        const parts = nzString.split(', ');
        if (parts[0] === dateStr && parts[1] === '00:00:00') {
          return testUTC;
        }
      }
      return baseUTC - (13 * 3600000);
    };

    const startOfDay = findNZMidnightForFilter(selectedDate);
    const endOfDay = startOfDay + (24 * 60 * 60 * 1000);

    // Include events from 12 hours before to 12 hours after the selected day
    // This ensures we have data points to render a smooth curve through the full 24 hours
    const displayStartTime = startOfDay - (12 * 60 * 60 * 1000);
    const displayEndTime = endOfDay + (12 * 60 * 60 * 1000);

    const displayEvents = tideData.tideEvents.filter(event => {
      const eventTime = new Date(event.time).getTime();
      return eventTime >= displayStartTime && eventTime <= displayEndTime;
    }).sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());

    return { dayEvents, displayEvents };
  }, [selectedDate, tideData.tideEvents]);

  // Calculate chart dimensions
  const chartHeight = 300;
  const chartWidth = 800;
  const padding = { top: 20, right: 40, bottom: 60, left: 60 };
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;

  // Get min/max values for scaling (from all display events)
  const minValue = useMemo(() => Math.min(...displayEvents.map(e => e.value)), [displayEvents]);
  const maxValue = useMemo(() => Math.max(...displayEvents.map(e => e.value)), [displayEvents]);
  const valueRange = maxValue - minValue;

  // Create points for the tide curve (use displayEvents which includes adjacent days)
  const points = useMemo(() => {
    if (displayEvents.length === 0) return [];

    // Find the UTC timestamp for midnight NZ time on the selected date
    // We need to find when it's 00:00 in Pacific/Auckland timezone
    const findNZMidnight = (dateStr: string) => {
      // Create a date in NZ timezone by using a date string
      // We'll iterate through possible UTC times to find which one corresponds to NZ midnight

      // Start from the selected date at UTC midnight and search around it
      const baseUTC = new Date(dateStr + 'T00:00:00Z').getTime();

      // NZ is ahead of UTC by 12 or 13 hours
      // So if it's Oct 14 00:00 in NZ, it's Oct 13 12:00 or 11:00 UTC
      // We need to go backwards from the UTC date

      for (let hourOffset = -14; hourOffset <= 14; hourOffset++) {
        const testUTC = baseUTC + (hourOffset * 3600000);

        // Format this UTC time in NZ timezone and check if it's midnight on our date
        const formatter = new Intl.DateTimeFormat('en-CA', {
          timeZone: 'Pacific/Auckland',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        });

        const nzString = formatter.format(new Date(testUTC));
        // Format: "2025-10-14, 00:00:00"
        const parts = nzString.split(', ');
        const nzDate = parts[0];
        const nzTime = parts[1];

        if (nzDate === dateStr && nzTime === '00:00:00') {
          return testUTC;
        }
      }

      // Fallback: approximate using fixed offset (NZ is typically UTC+13 in summer, UTC+12 in winter)
      // So midnight NZ = previous day at 11:00 or 12:00 UTC
      return baseUTC - (13 * 3600000);
    };

    const startOfDay = findNZMidnight(selectedDate);
    const endOfDay = startOfDay + (24 * 60 * 60 * 1000);
    const dayDuration = 24 * 60 * 60 * 1000;

    return displayEvents.map(event => {
      const time = new Date(event.time).getTime();
      // Map time to x coordinate (events outside the day will be off-chart but help curve)
      const x = ((time - startOfDay) / dayDuration) * innerWidth;
      const y = innerHeight - ((event.value - minValue) / valueRange) * innerHeight;

      // Mark if this point is within the visible day
      const isInDay = time >= startOfDay && time <= endOfDay;

      return { x, y, event, isInDay };
    });
  }, [displayEvents, selectedDate, minValue, valueRange, innerWidth, innerHeight]);

  // Create smooth curve path
  const curvePath = useMemo(() => {
    if (points.length < 2) return '';

    let path = `M ${points[0].x} ${points[0].y}`;

    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const curr = points[i];

      // Calculate control points for smooth bezier curve
      const cp1x = prev.x + (curr.x - prev.x) * 0.5;
      const cp1y = prev.y;
      const cp2x = curr.x - (curr.x - prev.x) * 0.5;
      const cp2y = curr.y;

      path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${curr.x} ${curr.y}`;
    }

    return path;
  }, [points]);

  // Format time for display (in New Zealand timezone)
  const formatTime = (timeStr: string) => {
    const date = new Date(timeStr);
    return date.toLocaleTimeString('en-NZ', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Pacific/Auckland'
    });
  };

  // Format date for display
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (dayEvents.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded p-8 text-center">
        <p className="text-gray-500">No tide data available for selected date</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Date Selector */}
      <div className="flex items-center gap-4">
        <label className="text-sm font-medium text-gray-700">Select Date:</label>
        <select
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          {availableDates.map(date => (
            <option key={date} value={date}>
              {formatDate(date)}
            </option>
          ))}
        </select>
        <span className="text-sm text-gray-500">
          ({availableDates.length} days available)
        </span>
      </div>

      {/* Chart Container */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Tide Heights - {formatDate(selectedDate)}
          </h3>
          <p className="text-sm text-gray-500">
            Datum: {tideData.metadata.datum} | Location: {tideData.metadata.actualLat.toFixed(4)}, {tideData.metadata.actualLong.toFixed(4)}
          </p>
        </div>

        {/* SVG Chart */}
        <svg
          width={chartWidth}
          height={chartHeight}
          className="mx-auto"
          style={{ maxWidth: '100%', height: 'auto' }}
        >
          <defs>
            <linearGradient id="tideGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.05" />
            </linearGradient>
          </defs>

          <g transform={`translate(${padding.left}, ${padding.top})`}>
            {/* Grid lines */}
            {[0, 0.25, 0.5, 0.75, 1].map(ratio => {
              const y = innerHeight * ratio;
              const value = maxValue - (valueRange * ratio);
              return (
                <g key={ratio}>
                  <line
                    x1={0}
                    y1={y}
                    x2={innerWidth}
                    y2={y}
                    stroke="#e5e7eb"
                    strokeWidth="1"
                  />
                  <text
                    x={-10}
                    y={y + 4}
                    textAnchor="end"
                    fontSize="12"
                    fill="#6b7280"
                  >
                    {value.toFixed(1)}m
                  </text>
                </g>
              );
            })}

            {/* Time axis labels */}
            {[0, 6, 12, 18, 24].map(hour => {
              const x = (hour / 24) * innerWidth;
              return (
                <g key={hour}>
                  <line
                    x1={x}
                    y1={innerHeight}
                    x2={x}
                    y2={innerHeight + 5}
                    stroke="#6b7280"
                    strokeWidth="1"
                  />
                  <text
                    x={x}
                    y={innerHeight + 20}
                    textAnchor="middle"
                    fontSize="12"
                    fill="#6b7280"
                  >
                    {hour === 0 ? '12 AM' : hour === 12 ? '12 PM' : hour > 12 ? `${hour - 12} PM` : `${hour} AM`}
                  </text>
                </g>
              );
            })}

            {/* Fill area under curve */}
            {curvePath && (
              <path
                d={`${curvePath} L ${points[points.length - 1].x} ${innerHeight} L ${points[0].x} ${innerHeight} Z`}
                fill="url(#tideGradient)"
              />
            )}

            {/* Tide curve */}
            {curvePath && (
              <path
                d={curvePath}
                fill="none"
                stroke="#3b82f6"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}

            {/* Points (only show points within the selected day) */}
            {points.filter(p => p.isInDay).map((point, index) => (
              <g key={index}>
                <circle
                  cx={point.x}
                  cy={point.y}
                  r="5"
                  fill={point.event.type === 'high' ? '#ef4444' : '#10b981'}
                  stroke="white"
                  strokeWidth="2"
                  className="cursor-pointer hover:r-7 transition-all"
                >
                  <title>
                    {point.event.type === 'high' ? 'High' : 'Low'} Tide: {point.event.value.toFixed(2)}m at {formatTime(point.event.time)}
                  </title>
                </circle>
              </g>
            ))}

            {/* Axis labels */}
            <text
              x={-innerHeight / 2}
              y={-45}
              transform="rotate(-90)"
              textAnchor="middle"
              fontSize="14"
              fontWeight="600"
              fill="#374151"
            >
              Tide Height (meters)
            </text>
            <text
              x={innerWidth / 2}
              y={innerHeight + 45}
              textAnchor="middle"
              fontSize="14"
              fontWeight="600"
              fill="#374151"
            >
              Time
            </text>
          </g>
        </svg>

        {/* Legend */}
        <div className="mt-4 flex items-center justify-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-red-500 border-2 border-white"></div>
            <span className="text-gray-700">High Tide</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-green-500 border-2 border-white"></div>
            <span className="text-gray-700">Low Tide</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-1 bg-blue-500"></div>
            <span className="text-gray-700">Tide Curve</span>
          </div>
        </div>
      </div>

      {/* Tide Events Table */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="text-md font-semibold text-gray-900 mb-3">Tide Schedule</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {dayEvents.map((event, index) => (
            <div
              key={index}
              className={`flex items-center justify-between p-3 rounded-lg ${
                event.type === 'high'
                  ? 'bg-red-50 border border-red-200'
                  : 'bg-green-50 border border-green-200'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${
                  event.type === 'high' ? 'bg-red-500' : 'bg-green-500'
                }`}></div>
                <div>
                  <div className="font-medium text-gray-900">
                    {event.type === 'high' ? 'High Tide' : 'Low Tide'}
                  </div>
                  <div className="text-sm text-gray-600">
                    {formatTime(event.time)}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-gray-900">
                  {event.value.toFixed(2)}m
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TideChart;
